'use client';

import { useEffect, useState } from 'react';
import { List, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ObraCard from '@/components/obra/ObraCard';
import { api } from '@/utils/api';
import { accentHexForFormato } from '@/constants/obras-generos';

const verTodasBtnClass =
    'flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-sm text-white/80 transition-all duration-200 ease-out hover:scale-105 hover:border-white/35 hover:bg-white/5 hover:text-white active:scale-100';

const GENEROS = [
    { id: 'comic', label: 'Comic', formato: 5, color: '#f97316' },
    { id: 'shoujo', label: 'Shoujo', formato: 4, color: '#a855f7' },
    { id: 'yaoi', label: 'Yaoi', formato: 8, color: '#ec4899' },
    { id: 'yuri', label: 'Yuri', formato: 9, color: '#3b82f6' },
    { id: 'hentai', label: 'Hentai', formato: 10, color: '#ef4444' },
    { id: 'novel', label: 'Novel', formato: 3, color: '#0ea5e9' },
];

/** Igual à app: até 12 cartões na fila horizontal. */
const ROW_DISPLAY_LIMIT = 12;

interface RecentesCriadasProps {
    formatoId?: number;
    /** Quando true, o formato vem da home; não exibe pills duplicadas. */
    controlledFormato?: boolean;
    onFormatoChange?: (formato: number) => void;
}

function ensureList(res: unknown): unknown[] {
    if (Array.isArray(res)) return res;
    if (res && typeof res === 'object') {
        const obj = res as { data?: unknown; items?: unknown };
        if (Array.isArray(obj.data)) return obj.data;
        if (Array.isArray(obj.items)) return obj.items;
    }
    return [];
}

function normalizeObraForList(obra: Record<string, unknown>) {
    const id = Number(obra.id ?? obra.obr_id);
    return {
        ...obra,
        id: Number.isFinite(id) ? id : obra.id,
        nome: String(obra.nome ?? obra.obr_nome ?? ''),
        imagem: obra.imagem ?? obra.obr_imagem,
        total_capitulos: obra.total_capitulos ?? obra.capitulos_count,
        capitulos_count: obra.capitulos_count ?? obra.total_capitulos,
    };
}

function HorizontalPosterRowSkeleton({ count = 8 }: { count?: number }) {
    return (
        <div className="relative -mx-4 md:-mx-8">
            <div className="flex gap-4 overflow-x-auto px-4 pb-4 md:px-8 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                {Array.from({ length: count }).map((_, i) => (
                    <div key={i} className="w-[140px] shrink-0 sm:w-[160px]">
                        <div className="mb-3 aspect-[2/3] w-full animate-pulse rounded-xl bg-white/[0.08]" />
                        <div className="h-3.5 w-full animate-pulse rounded bg-white/[0.06]" />
                        <div className="mt-1 h-3.5 w-2/3 animate-pulse rounded bg-white/[0.06]" />
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function RecentesCriadas({
    formatoId: initialFormatoId = 5,
    controlledFormato = false,
    onFormatoChange,
}: RecentesCriadasProps) {
    const navigate = useNavigate();
    const [selectedFormato, setSelectedFormato] = useState(initialFormatoId);
    const [obras, setObras] = useState<unknown[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (controlledFormato) setSelectedFormato(initialFormatoId);
    }, [controlledFormato, initialFormatoId]);

    const formatoAtual = controlledFormato ? initialFormatoId : selectedFormato;
    const accent = accentHexForFormato(formatoAtual);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            setLoading(true);
            try {
                const { data: raw } = await api.get(
                    `obras/adicionados-recentes?limit=24&offset=0&formato=${formatoAtual}`,
                );
                const list = ensureList(raw);
                if (!cancelled) setObras(list);
            } catch {
                if (!cancelled) setObras([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        const timeoutId = window.setTimeout(() => {
            void run();
        }, 150);
        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [formatoAtual]);

    const list = Array.isArray(obras) ? obras : [];

    return (
        <section className="relative animate-fadeIn py-4 delay-300 md:py-8">
            <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-start">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2">
                    <Sparkles className="h-5 w-5 shrink-0" style={{ color: accent }} />
                    <h2 className="relative inline-block shrink-0 text-2xl font-bold text-white">
                        Adicionados Recentemente
                        <span
                            className="absolute -bottom-2 left-0 h-1 w-1/3 rounded-full"
                            style={{ backgroundColor: accent }}
                        />
                    </h2>
                    {!loading && list.length > 0 && (
                        <button
                            type="button"
                            onClick={() =>
                                navigate(`/pesquisar?ordenarPor=criada_em_desc&formato=${formatoAtual}&pagina=1`)
                            }
                            className={verTodasBtnClass}
                        >
                            <List className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                            Ver todos
                        </button>
                    )}
                </div>

                {!controlledFormato && (
                    <div className="flex flex-wrap gap-2 md:shrink-0 md:justify-end">
                        {GENEROS.map((genero) => {
                            const isSelected = genero.formato === formatoAtual;
                            return (
                                <button
                                    key={genero.id}
                                    type="button"
                                    onClick={() =>
                                        onFormatoChange ? onFormatoChange(genero.formato) : setSelectedFormato(genero.formato)
                                    }
                                    className={`rounded-full border border-white/10 px-3 py-1.5 text-xs font-medium transition-all md:text-sm ${
                                        isSelected
                                            ? 'ring-1 ring-inset'
                                            : 'bg-transparent text-neutral-400 hover:bg-white/5 hover:text-white'
                                    }`}
                                    style={
                                        isSelected
                                            ? { backgroundColor: `${genero.color}20`, color: genero.color, borderColor: genero.color }
                                            : {}
                                    }
                                >
                                    {genero.label}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {loading && <HorizontalPosterRowSkeleton count={8} />}

            {!loading && list.length === 0 && (
                <div className="py-12 text-center">
                    <p className="text-lg text-white/50">Nenhuma obra adicionada recentemente.</p>
                </div>
            )}

            {!loading && list.length > 0 && (
                <>
                    <div className="relative -mx-4 md:-mx-8">
                        <div className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-20 bg-gradient-to-r from-[#000000] to-transparent" />
                        <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-20 bg-gradient-to-l from-[#000000] to-transparent" />
                        <div className="flex gap-4 overflow-x-auto px-4 pb-4 md:px-8 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                            {list.slice(0, ROW_DISPLAY_LIMIT).map((raw) => {
                                const obra = normalizeObraForList(raw as Record<string, unknown>);
                                const cap =
                                    typeof obra.total_capitulos === 'number' && Number.isFinite(obra.total_capitulos)
                                        ? obra.total_capitulos
                                        : typeof obra.capitulos_count === 'number' && Number.isFinite(obra.capitulos_count)
                                          ? obra.capitulos_count
                                          : undefined;
                                const id = typeof obra.id === 'number' && Number.isFinite(obra.id) ? obra.id : Number(obra.id);
                                return (
                                    <div key={id} className="w-[140px] shrink-0 sm:w-[160px]">
                                        <ObraCard
                                            size="small"
                                            coverLoading="eager"
                                            obra={{
                                                id,
                                                nome: obra.nome,
                                                imagem: obra.imagem as string | undefined,
                                                total_capitulos: cap ?? null,
                                                capitulos_count: cap ?? null,
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
        </section>
    );
}
